import apiClient from "./axiosConfig";

// The signed-in admin's own account: routes/profile.js on the server (mounted at /api/profile).
// Every endpoint acts on the account in the session token; none takes a user id.

/** A WhatsApp code that is waiting to be entered (survives a page reload). */
export interface PendingPhone {
  /** The number the code went to (your own), e.g. "+919876543210". */
  phoneNumber: string;
  /** "+91 ••••••3210" */
  phoneHint: string;
  /** True when it is for a new number; false when it verifies the current one. */
  change: boolean;
  expiresAt: string;
}

/** What GET /api/profile returns; the auth context keeps it as the signed-in user. */
export interface ProfileUser {
  _id: string;
  name: string;
  userName?: string;
  email?: string | null;
  role?: { name: string } | null;
  phoneNumber?: string | null;
  phoneVerified?: boolean;
  pendingPhone?: PendingPhone | null;
  /** Index into utils/ProfilePics, stored as a string. */
  profilePicIndex?: string | null;
  /** Uploaded avatar (data URL); shown instead of the stock photo. */
  photo?: string | null;
  /** Light or dark; null until the user picks one (the app then uses light). */
  themePreference?: "light" | "dark" | null;
  createdAt?: string | null;
  isActive?: boolean;
}

export interface DetailsInput {
  name?: string;
  userName?: string;
  email?: string;
  /** Needed when the username or email changes. */
  currPassword?: string;
}

export const updateProfileDetails = async (
  input: DetailsInput
): Promise<{ user: ProfileUser; changed: string[] }> => {
  const res = await apiClient.patch<{ user: ProfileUser; changed: string[] }>(
    "/api/profile",
    input
  );
  return res.data;
};

/** Changes the password and ends every other session; the answer carries this session's new token. */
export const changePassword = async (input: {
  currPassword: string;
  newPassword: string;
}): Promise<{ token: string; user: ProfileUser }> => {
  const res = await apiClient.post<{ token: string; user: ProfileUser }>(
    "/api/profile/password",
    input
  );
  return res.data;
};

export interface PhoneCodeResult {
  phoneHint: string;
  change: boolean;
  expiresInSeconds: number;
  resendAfterSeconds: number;
  user: ProfileUser;
}

/**
 * Sends a WhatsApp code: with no phoneNumber to verify the current number, or to a new number
 * (with the current password). The new number only replaces the current one once confirmed.
 */
export const sendPhoneCode = async (
  input: { phoneNumber?: string; currPassword?: string } = {}
): Promise<PhoneCodeResult> => {
  const res = await apiClient.post<PhoneCodeResult>("/api/profile/phone/code", input);
  return res.data;
};

export const confirmPhoneCode = async (
  code: string
): Promise<{ changed: boolean; user: ProfileUser }> => {
  const res = await apiClient.post<{ changed: boolean; user: ProfileUser }>(
    "/api/profile/phone/confirm",
    { code }
  );
  return res.data;
};

/** Saves an uploaded photo (a small data URL) or picks a stock photo. */
export const setProfilePhoto = async (
  input: { photo: string } | { profilePicIndex: number }
): Promise<ProfileUser> => {
  const res = await apiClient.put<ProfileUser>("/api/profile/photo", input);
  return res.data;
};

export const removeProfilePhoto = async (): Promise<ProfileUser> => {
  const res = await apiClient.delete<ProfileUser>("/api/profile/photo");
  return res.data;
};

/** Saves interface preferences on the account (the theme for now). */
export const savePreferences = async (input: { theme: "light" | "dark" }): Promise<ProfileUser> => {
  const res = await apiClient.patch<ProfileUser>("/api/profile/preferences", input);
  return res.data;
};
